import { useState, useEffect } from "react";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useWaitlistSubmit } from "@/hooks/useWaitlistSubmit";
import { getPendingEditSource, clearPendingEditSource } from "./pendingEditSource";

interface WaitlistDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Dialog heading. Defaults to the "we're full" copy used site-wide. */
    title?: string;
    /** Show the "$X OFF as a thank-you" line. */
    showDiscountNote?: boolean;
    /** The highlighted amount in that line. Keep in sync with the page's banner. */
    discountLabel?: string;
    /** Which qualifying question to ask. */
    sourceField?: "hear-options" | "grad-year";
    /** Label for the grad-year dropdown; also the prefix stored in how_did_you_hear. */
    gradYearLabel?: string;
    /**
     * Tags the signup with the page it came from (e.g. "wharton"), which decides
     * which welcome email is sent. Omit on the main site.
     */
    signupSource?: string;
}

// Where people heard about us — tuned to our real acquisition channels.
// `examples` is display-only helper text; only `value` is stored.
const HEAR_OPTIONS: { value: string; label: string; examples?: string }[] = [
    {
        value: "Brand box partnership",
        label: "Brand box partnership",
        examples: "Wine.com, Dollar Shave Club, Hello Fresh",
    },
    { value: "Local flyer / advertisement", label: "Local flyer / advertisement" },
    { value: "Podcast / newsletter", label: "Podcast / newsletter" },
    { value: "Friend / Referral", label: "Friend / Referral" },
    { value: "Other", label: "Other" },
];

// Selecting this reveals the "who referred you" field so we can thank them.
const REFERRAL_OPTION = "Friend / Referral";

// Selecting this reveals a free-text box to capture a source we didn't list.
const OTHER_OPTION = "Other";

const slug = (s: string) => s.replace(/\s+/g, "-").toLowerCase();

// Newest class first — campaign pages ask for a graduation year, not a channel.
const GRAD_YEAR_MAX = 2028;
const GRAD_YEAR_MIN = 1960;
const GRAD_YEARS = Array.from(
    { length: GRAD_YEAR_MAX - GRAD_YEAR_MIN + 1 },
    (_, i) => String(GRAD_YEAR_MAX - i)
);

const WaitlistDialog = ({
    open,
    onOpenChange,
    title = "We're excited to style you!",
    showDiscountNote = true,
    discountLabel = "$30 OFF",
    sourceField = "hear-options",
    gradYearLabel = "Wharton graduation year",
    signupSource,
}: WaitlistDialogProps) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [sources, setSources] = useState<string[]>([]);
    const [referrer, setReferrer] = useState("");
    const [otherText, setOtherText] = useState("");
    const [gradYear, setGradYear] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    // Which edit (if any) they clicked "Book" on before opening this dialog —
    // captured on open so we can call it out; cleared on close.
    const [editLabel, setEditLabel] = useState<string | null>(null);
    const { submit } = useWaitlistSubmit();

    // Capture the stashed edit on open (to greet with it), and wipe it on close
    // so a later waitlist open from a non-edit CTA records edit_source = null.
    useEffect(() => {
        if (open) setEditLabel(getPendingEditSource());
        else clearPendingEditSource();
    }, [open]);

    const referredByFriend = sources.includes(REFERRAL_OPTION);
    const otherSelected = sources.includes(OTHER_OPTION);

    const toggleSource = (opt: string) =>
        setSources((prev) =>
            prev.includes(opt) ? prev.filter((s) => s !== opt) : [...prev, opt]
        );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your email");
            return;
        }
        if (sourceField === "grad-year" && !gradYear) {
            toast.error(`Please select your ${gradYearLabel}`);
            return;
        }
        setIsSubmitting(true);
        try {
            const trimmedReferrer = referrer.trim();
            const trimmedOther = otherText.trim();
            // Expand "Other" into the free-text detail when they gave one.
            const hearParts = sources.map((s) =>
                s === OTHER_OPTION && trimmedOther ? `Other: ${trimmedOther}` : s
            );
            const result = await submit({
                email,
                name,
                howDidYouHear:
                    sourceField === "grad-year"
                        ? `${gradYearLabel}: ${gradYear}`
                        : hearParts.length
                            ? hearParts.join(", ")
                            : null,
                // Capture the referrer's name so we can thank them later.
                comments:
                    sourceField === "hear-options" && referredByFriend && trimmedReferrer
                        ? `Referred by: ${trimmedReferrer}`
                        : null,
                source: signupSource ?? null,
                // Which pricing edit they booked (if any), stashed by PricingEdits.
                editSource: getPendingEditSource(),
            });

            if (result.alreadyOnList) {
                toast.info("You're already on our waitlist!");
                return;
            }
            setIsSuccess(true);
            // Keep the form values until the dialog closes so the success card can echo the email.
        } catch (error) {
            console.error("Error submitting to waitlist:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Below md this is a bottom sheet: the max-md: overrides only touch
                DialogContent's unprefixed base classes (its one sm: class, the
                radius, needs the !), and the dialog's own responsive classes are
                written as base + md: so nothing fights in the 640–767 band. */}
            <DialogContent className="max-md:left-0 max-md:right-0 max-md:top-auto max-md:bottom-0 max-md:translate-x-0 max-md:translate-y-0 max-md:max-w-none max-md:!rounded-t-2xl max-md:!rounded-b-none max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=open]:slide-in-from-right-0 max-md:data-[state=open]:zoom-in-100 max-md:data-[state=open]:fade-in-100 max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=closed]:slide-out-to-right-0 max-md:data-[state=closed]:zoom-out-100 max-md:data-[state=closed]:fade-out-100 max-md:data-[state=open]:duration-300 max-md:data-[state=closed]:duration-200 max-md:[&>button]:hidden md:max-w-md bg-white md:bg-[#F5F5F4] border-x-0 border-b-0 border-t border-stone-200 md:border shadow-[0_-20px_40px_-20px_rgba(15,23,42,0.3)] md:shadow-2xl px-6 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:p-10 max-h-[90dvh] overflow-y-auto overscroll-contain">
                <DialogHeader className="mb-2 max-md:flex-row max-md:items-center max-md:justify-between max-md:space-y-0">
                    <DialogTitle className="text-[18px] leading-[1.3] font-playfair font-bold text-[#0f172a] text-left not-italic tracking-normal md:text-4xl md:leading-10 md:font-semibold md:text-[#0a0502] md:text-center md:italic md:tracking-tight">
                        {editLabel ? `Got it — the ${editLabel} edit.` : title}
                    </DialogTitle>
                    <DialogClose className="md:hidden -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[28px] font-light leading-none text-[#78716c] active:bg-stone-100">
                        <span aria-hidden="true">×</span>
                        <span className="sr-only">Close</span>
                    </DialogClose>
                </DialogHeader>

                {showDiscountNote && (
                    <p className="text-left md:text-center text-[15px] md:text-[16px] font-playfair text-[#0a0502] mb-1">
                        Get <span className="font-bold text-[#FF8B11]">{discountLabel}</span> as a thank-you for your patience
                    </p>
                )}

                {!isSuccess ? (
                    <div className="space-y-5">
                        <p className="text-[#0a0502]/60 text-left md:text-center text-[13px] md:text-[14px] font-sans leading-relaxed">
                            {editLabel
                                ? "We're excited to style you! We're full at the moment — add your email and we'll reach out the moment a spot opens up."
                                : "We're full at the moment. We'll email you the moment a spot opens up."}
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-3">
                                <Input
                                    type="text"
                                    placeholder="Name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    className="bg-white border-stone-200 focus:border-stone-400 h-12 px-4 text-[16px] md:text-[14px] rounded-md shadow-sm"
                                    disabled={isSubmitting}
                                />

                                <Input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="bg-white border-stone-200 focus:border-stone-400 h-12 px-4 text-[16px] md:text-[14px] rounded-md shadow-sm"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {sourceField === "grad-year" ? (
                                <fieldset className="space-y-2 border-t border-stone-200 pt-3" disabled={isSubmitting}>
                                    <legend className="text-[11px] font-medium text-[#0a0502]/55">
                                        {gradYearLabel}
                                    </legend>
                                    <Select value={gradYear} onValueChange={setGradYear} disabled={isSubmitting}>
                                        <SelectTrigger
                                            aria-label={gradYearLabel}
                                            className="bg-white border-stone-200 focus:border-stone-400 h-12 px-4 text-[16px] md:text-[14px] rounded-md shadow-sm"
                                        >
                                            <SelectValue placeholder="Select a year" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[300px]">
                                            {GRAD_YEARS.map((year) => (
                                                <SelectItem key={year} value={year}>
                                                    {year}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </fieldset>
                            ) : (
                            <fieldset className="space-y-2 border-t border-stone-200 pt-3" disabled={isSubmitting}>
                                <legend className="text-[11px] font-medium text-[#0a0502]/55">
                                    How did you hear about us?{" "}
                                    <span className="text-[#0a0502]/35 font-normal">(optional)</span>
                                </legend>
                                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                    {HEAR_OPTIONS.map((opt) => (
                                        <div key={opt.value} className="flex items-center gap-1.5">
                                            <Checkbox
                                                id={`hear-${slug(opt.value)}`}
                                                checked={sources.includes(opt.value)}
                                                onCheckedChange={() => toggleSource(opt.value)}
                                                className="h-[14px] w-[14px] shrink-0 rounded-[4px] border-stone-300 data-[state=checked]:bg-[#0a0502] data-[state=checked]:border-[#0a0502]"
                                            />
                                            <Label
                                                htmlFor={`hear-${slug(opt.value)}`}
                                                className="cursor-pointer leading-tight"
                                            >
                                                <span className="block text-[11.5px] text-[#0a0502]/85 font-normal">
                                                    {opt.label}
                                                </span>
                                            </Label>
                                        </div>
                                    ))}
                                </div>

                                <AnimatePresence initial={false}>
                                    {referredByFriend && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <Input
                                                type="text"
                                                placeholder="Who referred you? (so we can thank them)"
                                                value={referrer}
                                                onChange={e => setReferrer(e.target.value)}
                                                className="mt-1 bg-white border-stone-200 focus:border-stone-400 h-12 px-4 text-[16px] md:text-[14px] rounded-md shadow-sm"
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <AnimatePresence initial={false}>
                                    {otherSelected && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <Input
                                                type="text"
                                                placeholder="Where did you hear about us?"
                                                value={otherText}
                                                onChange={e => setOtherText(e.target.value)}
                                                className="mt-1 bg-white border-stone-200 focus:border-stone-400 h-12 px-4 text-[16px] md:text-[14px] rounded-md shadow-sm"
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </fieldset>
                            )}

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-[#FF8B11] hover:bg-[#FF8B11]/90 shadow-[inset_0_-2px_0_#c2410c] h-12 md:bg-[#0a0502] md:hover:bg-stone-800 md:shadow-md md:h-14 text-white text-[11px] font-bold uppercase tracking-[0.15em] rounded-md transition-all active:scale-[0.98]"
                            >
                                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
                            </Button>
                        </form>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="py-6 flex flex-col items-center justify-center space-y-6"
                    >
                        <div className="w-16 h-16 rounded-full bg-[#155e30]/10 flex items-center justify-center">
                            <svg className="w-8 h-8 text-[#155e30]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>

                        <div className="text-center space-y-2 px-4">
                            <h3 className="text-xl font-playfair font-bold text-[#0a0502]">You're on the list.</h3>
                            <p className="text-[#0a0502]/60 text-sm max-w-[280px] mx-auto leading-relaxed">
                                We'll email you at <span className="font-medium text-[#0a0502]">{email}</span> as soon as a spot opens up in our next cohort.
                            </p>
                        </div>

                        <Button
                            onClick={() => {
                                onOpenChange(false);
                                setTimeout(() => setIsSuccess(false), 500);
                            }}
                            className="bg-stone-200 hover:bg-stone-300 text-[#0a0502] w-full max-w-[200px] mt-4 font-bold tracking-widest uppercase text-[10px]"
                        >
                            Got It
                        </Button>
                    </motion.div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default WaitlistDialog;
